import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';

/**
 * Custom columns vivem GLOBAIS por organização. Editar uma muda imediatamente
 * em todos os presets que referenciam — esse é o ponto do hook (vs antes, que
 * cada preset tinha sua cópia inline da expressão).
 *
 * Shape exposto:
 *   - `columns`: array<{ id, name, expression, format, ... }>
 *   - `byId`: Map<id, column> pra lookup O(1) no render da tabela
 *   - `loading` / `error`: estado do GET inicial
 *   - `createColumn(data)`: cria + insere no estado local
 *   - `updateColumn(id, patch)`: edita + propaga
 *   - `deleteColumn(id)`: remove + caller deve limpar refs em presets
 *
 * Sem debounce no save — diferente do useUserPreferences, edições aqui são
 * raras e o user espera feedback imediato (preview da expression depende).
 */
export function useCustomColumns() {
    const [columns, setColumns] = useState([]);
    const [byId, setById] = useState(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const inFlight = useRef(false);

    const indexById = useCallback((list) => {
        const map = new Map();
        for (const c of list) map.set(c.id, c);
        return map;
    }, []);

    const load = useCallback(async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setLoading(true);
        setError(null);
        try {
            const resp = await api.listCustomColumns();
            const list = Array.isArray(resp) ? resp : resp?.results || [];
            setColumns(list);
            setById(indexById(list));
        } catch (err) {
            setError({
                message: err?.message || 'Não foi possível carregar colunas customizadas.',
                code: err?.code || 'OPS28_CUSTOM_COLS_LOAD',
            });
            console.error('[operacoes/customColumns/load]', err?.code || 'OPS28_CUSTOM_COLS_LOAD', err);
        } finally {
            setLoading(false);
            inFlight.current = false;
        }
    }, [indexById]);

    useEffect(() => {
        load();
    }, [load]);

    const createColumn = useCallback(
        async (data) => {
            const created = await api.createCustomColumn(data);
            setColumns((prev) => {
                const next = [...prev, created];
                setById(indexById(next));
                return next;
            });
            return created;
        },
        [indexById],
    );

    const updateColumn = useCallback(
        async (id, patch) => {
            const updated = await api.updateCustomColumn(id, patch);
            setColumns((prev) => {
                const next = prev.map((c) => (c.id === id ? updated : c));
                setById(indexById(next));
                return next;
            });
            return updated;
        },
        [indexById],
    );

    const deleteColumn = useCallback(
        async (id) => {
            await api.deleteCustomColumn(id);
            setColumns((prev) => {
                const next = prev.filter((c) => c.id !== id);
                setById(indexById(next));
                return next;
            });
        },
        [indexById],
    );

    return {
        columns,
        byId,
        loading,
        error,
        reload: load,
        createColumn,
        updateColumn,
        deleteColumn,
    };
}

/**
 * Helpers pra trabalhar com refs `custom:<id>` no array `preset.columns`.
 * Mantemos fora do hook pra poder usar em qualquer lugar (data.js, render, etc).
 */
const CUSTOM_REF_PREFIX = 'custom:';

export function IsCustomRef(col) {
    return typeof col === 'string' && col.startsWith(CUSTOM_REF_PREFIX);
}

export function CustomRefToId(col) {
    if (!IsCustomRef(col)) return null;
    return col.slice(CUSTOM_REF_PREFIX.length);
}

export function IdToCustomRef(id) {
    return `${CUSTOM_REF_PREFIX}${id}`;
}
